import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('token')));

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setLoading(false);
    }, []);

    const fetchUserData = useCallback(async (authToken) => {
        const response = await fetch(`${API_URL}/v1/users/me`, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            /* ignore non-json */
        }

        if (!response.ok) {
            if (data?.detail === 'User is not verified') {
                throw new Error('User is not verified');
            }
            throw new Error(
                typeof data?.detail === 'string' ? data.detail : 'Failed to fetch user'
            );
        }

        if (!data?.is_verified) {
            throw new Error('User is not verified');
        }

        setUser(data);
        return data;
    }, []);

    // Восстановление сессии только при первом монтировании
    useEffect(() => {
        let cancelled = false;
        const existing = localStorage.getItem('token');

        if (!existing) {
            setLoading(false);
            return undefined;
        }

        (async () => {
            try {
                await fetchUserData(existing);
                if (!cancelled) setToken(existing);
            } catch (error) {
                console.error('Error fetching user data:', error);
                if (!cancelled) {
                    localStorage.removeItem('token');
                    setToken(null);
                    setUser(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fetchUserData]);

    const login = async (email, password) => {
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        const response = await fetch(`${API_URL}/v1/auth/token`, {
            method: 'POST',
            body: formData,
        });

        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            throw new Error('Ошибка ответа сервера');
        }

        if (!response.ok) {
            if (data?.detail === 'User is not verified') {
                throw new Error('User is not verified');
            }
            return false;
        }

        if (!data?.access_token) {
            return false;
        }

        // Сначала получаем user — иначе PrivateRoute сразу кидает на /login
        try {
            await fetchUserData(data.access_token);
        } catch (error) {
            localStorage.removeItem('token');
            setUser(null);
            throw error;
        }

        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        setLoading(false);
        return true;
    };

    const register = async (username, email, password) => {
        const response = await fetch(`${API_URL}/v1/auth/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, email, password }),
        });

        if (response.ok) {
            return true;
        }

        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            return false;
        }

        if (data?.detail === 'Email already registered') {
            throw new Error('Email already registered');
        }
        if (Array.isArray(data?.detail)) {
            throw new Error(data.detail[0]?.msg || 'Ошибка регистрации');
        }
        if (typeof data?.detail === 'string') {
            throw new Error(data.detail);
        }
        return false;
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, register, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
